import React, { Fragment, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { flow, get, omit } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { useCoreSrpFilter } from 'site-modules/shared/hooks/core-inventory/use-core-srp-filter';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { PageModel } from 'client/data/models/page';
import { venomHistory } from 'client/utils/history/venom-history';
import { getSrpBrowserQuery } from 'site-modules/shared/utils/inventory/srp-utils';
import { INVENTORY_TYPES_LOWERCASE } from 'client/constants/inventory-types';
import {
  BATTERY_RANGE_SCORE,
  BODY_TYPE,
  COMBINE_MPG,
  ELECTRICITY_RANGE,
  INTERIOR_COLOR,
  INVENTORY_TYPE,
  RADIUS,
  SORT_BY,
  TOTAL_SEATING,
} from 'site-modules/shared/constants/allowed-inventory-request-params';
import { expandedRadiusDueToLowResultsSort } from 'site-modules/shared/constants/sort-types';
import { getSelectedPaymentType } from 'site-modules/shared/utils/inventory-utils/get-current-payment-type';
import { transformSelectedFacetsForDisplaying } from 'site-modules/shared/utils/inventory/search-filter';
import { srpLinkBuilderWithParamsConversion } from 'site-modules/shared/utils/srp-link-constructor';
import { withListTrackingData } from 'site-modules/shared/components/inventory-list-tracking/list-tracking-data';
import { withInventoryListTracking } from 'site-modules/shared/components/inventory-list-tracking/inventory-list-tracking';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { Link } from 'site-modules/shared/components/link/link';
import { Zip } from 'site-modules/shared/components/zip/zip';
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { SuggestedFacets } from 'site-modules/shared/components/inventory/suggested-facets/suggested-facets';
import { QuickFilters } from 'site-modules/shared/components/inventory/quick-filters/quick-filters';
import { UsurpFilters } from 'site-modules/shared/components/inventory/usurp-filters/usurp-filters';
import { UsurpFiltersDrawer } from 'site-modules/shared/components/inventory/usurp-filters-drawer/usurp-filters-drawer';
import { UsurpInventoryCard } from 'site-modules/shared/components/inventory/usurp-inventory-card/usurp-inventory-card';
import { SrpCardsTopPanel } from 'site-modules/shared/components/inventory/srp-cards-top-panel/srp-cards-top-panel';
import { InventoryDependencyContext } from 'site-modules/shared/components/inventory/inventory-dependency/inventory-dependency-context';
import { usurpDependencyContextValue } from 'site-modules/shared/components/inventory/inventory-dependency/usurp-dependency-context-value';

import './cpo-srp.scss';

export const CPO_SRP_INITIAL_FILTER = { inventoryType: [INVENTORY_TYPES_LOWERCASE.CPO] };
const CONSTANT_FILTERS = ['inventoryType', 'initialUrlPattern', 'pageSize'];
const EXCLUDED_FILTER_TYPES = [
  INVENTORY_TYPE,
  BODY_TYPE,
  COMBINE_MPG,
  ELECTRICITY_RANGE,
  RADIUS,
  INTERIOR_COLOR,
  TOTAL_SEATING,
  BATTERY_RANGE_SCORE,
];
const CREATIVE_ID = 'edm-entry-inventory-cards';

export function addFiltersToQuery(pathname, filter) {
  const query = getSrpBrowserQuery(omit(filter, CONSTANT_FILTERS));

  venomHistory.push(`${pathname}${query ? `?${query}` : ''}`);
}

export function fireTrackingZipChange(value) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_PROVIDE_ZIP_CODE,
      subaction_name: TrackingConstant.ACTION_PROVIDE_ZIP_CODE,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
      creative_id: CREATIVE_ID,
      value,
    },
  });
}

export function CpoSrpUI({
  className = null,
  inventoryData = null,
  selectedFacets = null,
  visitorLocation = null,
  location = null,
  setModelValue,
  zipCode = null,
  isMobile = false,
}) {
  const containerRef = useRef();
  const [isStickyEnabled] = useOnScreen(containerRef);

  const { city, stateCode } = visitorLocation;
  const isInternational = isUnknownZip(visitorLocation);
  const { totalNumber: inventoryCount } = get(inventoryData, 'inventories', {});
  const facets = get(inventoryData, 'facets', []).filter(({ type }) => !EXCLUDED_FILTER_TYPES.includes(type));
  const displayInventories = get(inventoryData, 'inventories.results') && inventoryData.inventories.results.slice(0, 2);
  const suggestedFacets = get(inventoryData, 'suggestedFacets', []);
  const isExpandedRadiusDueToLowResults = !!get(inventoryData, 'attributes.expandedRadiusDueToLowResults', false);
  const attrRadius = get(inventoryData, 'attributes.radius');
  const isExpandedRadiusDueToLowResultsSort = isExpandedRadiusDueToLowResults && attrRadius === 6000;

  const {
    filteringByFacets,
    radius,
    onUpdateFilter,
    onFiltersButtonClick,
    closeFiltersDrawer,
    cancelFiltersUpdate,
    updateZipCode,
    isFilterDrawerOpen,
    isLoading,
  } = useCoreSrpFilter({
    inventoryData,
    facets,
    selectedFacets,
    initialFilter: CPO_SRP_INITIAL_FILTER,
    pathname: location.pathname,
    visitorLocation,
    addFiltersToQuery,
    setModelValue,
  });

  const renderSuggestedFacetsComponent = useCallback(
    suggestedFacetsToRender => (
      <SuggestedFacets
        suggestedFacets={suggestedFacetsToRender.filter(({ type }) => !EXCLUDED_FILTER_TYPES.includes(type))}
        onSuggestedFacetClick={onUpdateFilter}
        selectedFacets={selectedFacets}
      />
    ),
    [onUpdateFilter, selectedFacets]
  );

  const renderFilters = useCallback(
    sticky => (
      <div className={classnames({ 'filters-container rounded-12 bg-white px-0_25 py-0_5 mb-1_5': !sticky })}>
        <QuickFilters
          filtersButtonClassName=""
          listClassName="px-0_25 py-0_25 pb-lg-0_25"
          suggestedFacets={suggestedFacets}
          facets={facets}
          sticky={sticky}
          activeFiltersCount={transformSelectedFacetsForDisplaying(filteringByFacets).length}
          onFiltersBtnClick={onFiltersButtonClick}
          withFiltersBtn
          renderSuggestedFacetsComponent={renderSuggestedFacetsComponent}
        />
      </div>
    ),
    [facets, filteringByFacets, onFiltersButtonClick, renderSuggestedFacetsComponent, suggestedFacets]
  );

  if (!inventoryData) {
    return null;
  }

  return (
    <InventoryDependencyContext.Provider value={usurpDependencyContextValue}>
      <h2 className={classnames('heading-3 mb-1_5', { 'text-center': !isMobile, 'px-1': isMobile })}>
        Shop Certified Pre Owned Cars
      </h2>
      <div
        ref={containerRef}
        className={classnames('cpo-srp bg-cool-gray-90 mx-auto px-0_5 px-md-1 py-1_5', className, {
          'rounded-12': !isMobile,
        })}
        data-tracking-parent={CREATIVE_ID}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-1">
          {isInternational ? (
            <h3 className="heading-3 mb-0_5">For sale nearby</h3>
          ) : (
            <div className="mb-0_5">
              <h3 className="heading-3 d-inline me-0_5">
                For sale near {city}, {stateCode}
              </h3>{' '}
              <Zip
                zip={zipCode}
                onUpdate={updateZipCode}
                buttonClassName="size-16 text-blue-40 fw-normal text-transform-none"
                buttonText="Change location"
              />
            </div>
          )}
        </div>
        <LoadingSpinner isLoading={isLoading}>
          <AnimatedSticky
            stickyClassName="py-0_5 px-1 px-md-1_75"
            stickyFragment={renderFilters}
            hideSticky={!inventoryCount || !isStickyEnabled}
          >
            {renderFilters()}
          </AnimatedSticky>

          {!!inventoryCount && (
            <Fragment>
              <SrpCardsTopPanel
                onUpdate={onUpdateFilter}
                inventoryCount={inventoryCount}
                isMobile={isMobile}
                withRange={!!facets.find(({ type }) => type === ELECTRICITY_RANGE)}
                selectedFacets={
                  !get(selectedFacets, 'sortBy') && isExpandedRadiusDueToLowResultsSort
                    ? { ...selectedFacets, [SORT_BY]: [expandedRadiusDueToLowResultsSort] }
                    : selectedFacets
                }
              />
              <Row tag="ul" className="list-unstyled mb-0_5" data-tracking-parent="edm-entry-vehicle-card-list">
                {displayInventories?.map(inventory => (
                  <Col key={inventory.vin} tag="li" xs={12} md={6} className="mb-1_5">
                    <UsurpInventoryCard vehicle={inventory} radius={radius} lazyLoadImage isVinPreviewLink />
                  </Col>
                ))}
              </Row>
            </Fragment>
          )}

          {!inventoryCount && <div className="mb-2">Sorry, there are no vehicles for sale in our network.</div>}

          <div className="text-center">
            <Link
              to={srpLinkBuilderWithParamsConversion(selectedFacets)}
              rel="nofollow"
              className="text-blue-40"
              data-tracking-id="view_search_result"
            >
              See all for sale
              <i className="icon-arrow-right3 d-inline-block size-10 ms-0_25" aria-hidden />
            </Link>
          </div>
        </LoadingSpinner>

        <UsurpFiltersDrawer
          isOpen={isFilterDrawerOpen}
          onToggle={closeFiltersDrawer}
          onCancel={cancelFiltersUpdate}
          onApply={closeFiltersDrawer}
          searchResultsFound={inventoryCount}
          isLoading={isLoading}
          dataTrackingParent="srp-new-drawer-facets"
          drawerContainerClasses="cpo-srp-filters-drawer"
          isMobile={isMobile}
          filtersComponent={UsurpFilters}
          paymentType={getSelectedPaymentType(selectedFacets)}
          facets={facets}
          filteringByFacets={filteringByFacets}
          onUpdate={onUpdateFilter}
          zipCode={zipCode}
          onZipCodeUpdate={updateZipCode}
          selectedFacets={selectedFacets}
        />
      </div>
    </InventoryDependencyContext.Provider>
  );
}

CpoSrpUI.propTypes = {
  setModelValue: PropTypes.func.isRequired,
  className: PropTypes.string,
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
  }),
  visitorLocation: VisitorEntities.Location,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  zipCode: PropTypes.string,
  selectedFacets: PropTypes.shape({}),
  isMobile: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { inventoryData }) => !inventoryData;
export const CpoSrp = flow(withInventoryListTracking, withListTrackingData, component =>
  connectToModel(component, {
    zipCode: bindToPath('location.zipCode', VisitorModel),
    visitorLocation: bindToPath('location', VisitorModel, null, false),
    location: bindToPath('location', PageModel),
    inventoryData: bindToPath('searchResults', InventoryModel, null, false),
    selectedFacets: bindToPath('searchResultsFilter', InventoryModel, null, false),
  })
)(React.memo(CpoSrpUI, propsAreEqual));
